import { useState } from 'react';

import { useI18n } from '../../lib/use-i18n.js';
import { logger } from '../../lib/logger.js';
import { submitHelpFeedback } from '../../lib/repositories/help-feedback-repo.js';
import type { HelpSlug } from './articles.js';

/**
 * "Was this helpful?" widget at the foot of every /help article.
 *
 * Flow (no-dead-ends: every state has copy + a next action):
 *   idle     — prompt + thumbs up / thumbs down
 *   comment  — thumbs-down opens an OPTIONAL note (≤500 chars) before sending
 *   sending  — buttons disabled, explicit "sending" copy
 *   sent     — thank-you copy; sessionStorage guard set
 *   failed   — explicit failure copy + retry (keeps the vote + note)
 *
 * One submission per article per browser session via sessionStorage
 * (`helpFeedback.sent.{slug}`) — a returning reader in the same session sees
 * the already-thanked state instead of voting twice. Mount the component with
 * `key={slug}` so state resets when the reader navigates between articles.
 *
 * Writes go through submitHelpFeedback (anonymous client-SDK create validated
 * by firestore.rules) — see lib/repositories/help-feedback-repo.ts.
 */

const COMMENT_MAX = 500;

function sentKey(slug: HelpSlug): string {
  return `helpFeedback.sent.${slug}`;
}

function hasAlreadySent(slug: HelpSlug): boolean {
  try {
    return sessionStorage.getItem(sentKey(slug)) === '1';
  } catch {
    return false; // storage unavailable (e.g. blocked) — just allow the vote
  }
}

function markSent(slug: HelpSlug): void {
  try {
    sessionStorage.setItem(sentKey(slug), '1');
  } catch {
    // Non-fatal: the vote still landed; only the same-session guard is lost.
  }
}

type Stage =
  | { readonly kind: 'idle' }
  | { readonly kind: 'comment' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'sent'; readonly already: boolean }
  | { readonly kind: 'failed'; readonly helpful: boolean };

export function HelpFeedbackWidget({ slug }: { readonly slug: HelpSlug }) {
  const { t, locale } = useI18n();
  const [stage, setStage] = useState<Stage>(() =>
    hasAlreadySent(slug) ? { kind: 'sent', already: true } : { kind: 'idle' },
  );
  const [comment, setComment] = useState('');

  async function send(helpful: boolean): Promise<void> {
    setStage({ kind: 'sending' });
    const trimmed = comment.trim().slice(0, COMMENT_MAX);
    try {
      await submitHelpFeedback({
        slug,
        locale,
        helpful,
        ...(trimmed.length > 0 ? { comment: trimmed } : {}),
      });
      markSent(slug);
      setStage({ kind: 'sent', already: false });
    } catch (e) {
      logger.error('help feedback send failed', { slug, error: String(e) });
      setStage({ kind: 'failed', helpful });
    }
  }

  return (
    <section aria-live="polite" style={boxStyle}>
      {stage.kind === 'sent' ? (
        <p style={statusTextStyle}>
          {stage.already ? t('marketing.help.feedback.already') : t('marketing.help.feedback.sent')}
        </p>
      ) : (
        <>
          <p style={promptStyle}>{t('marketing.help.feedback.prompt')}</p>

          {stage.kind === 'idle' ? (
            <div style={buttonRowStyle}>
              <button type="button" style={choiceButtonStyle} onClick={() => void send(true)}>
                {t('marketing.help.feedback.yes')}
              </button>
              <button
                type="button"
                style={choiceButtonStyle}
                onClick={() => setStage({ kind: 'comment' })}
              >
                {t('marketing.help.feedback.no')}
              </button>
            </div>
          ) : null}

          {stage.kind === 'comment' || (stage.kind === 'failed' && !stage.helpful) ? (
            <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <span style={commentLabelStyle}>{t('marketing.help.feedback.commentLabel')}</span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={COMMENT_MAX}
                rows={3}
                placeholder={t('marketing.help.feedback.commentPlaceholder')}
                style={textareaStyle}
              />
            </label>
          ) : null}

          {stage.kind === 'comment' ? (
            <div style={buttonRowStyle}>
              <button type="button" style={sendButtonStyle} onClick={() => void send(false)}>
                {t('marketing.help.feedback.send')}
              </button>
            </div>
          ) : null}

          {stage.kind === 'sending' ? (
            <p style={statusTextStyle}>{t('marketing.help.feedback.sending')}</p>
          ) : null}

          {stage.kind === 'failed' ? (
            <div style={{ display: 'grid', gap: 'var(--space-2)', justifyItems: 'start' }}>
              <p style={statusTextStyle}>{t('marketing.help.feedback.failed')}</p>
              <button
                type="button"
                style={choiceButtonStyle}
                onClick={() => void send(stage.helpful)}
              >
                {t('marketing.help.feedback.retry')}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

// ── styles (marketing tokens; mirrors the article page's quiet surfaces) ──

const boxStyle: React.CSSProperties = {
  marginTop: 'var(--space-10)',
  padding: 'var(--space-5)',
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface)',
  display: 'grid',
  gap: 'var(--space-3)',
};

const promptStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 600,
  lineHeight: 1.35,
};

const statusTextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-3)',
};

const choiceButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface)',
  padding: '0.5em 1.25em',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
  minHeight: 'var(--touch-target, 44px)',
};

const sendButtonStyle: React.CSSProperties = {
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  padding: '0.5em 1.5em',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  cursor: 'pointer',
  minHeight: 'var(--touch-target, 44px)',
};

const commentLabelStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  color: 'var(--color-on-surface-muted)',
};

const textareaStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-body)',
  fontFamily: 'inherit',
  resize: 'vertical',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
};
